"use client";

import Link from "next/link";
import { Bell } from "lucide-react";
import { DockSheet } from "@/components/ui/dock-sheet";
import { DialogCard } from "@/components/ui/dialog-card";
import { Button } from "@/components/ui/button";
import { ItemCard } from "@/components/search/item-card";
import { useMobile } from "@/hooks/use-mobile";

interface NotificationConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentUserId?: string;
  listing: {
    id: string;
    title: string;
    price: string;
    location: string;
    image_urls: string[];
    description: string;
    condition: string;
    grade: string;
    created_at: string;
    username?: string;
    avatar_url?: string | null;
    scheduledPublishAt?: string | null;
    currency?: string | null;
    quantity?: number;
    listing_format?: string | null;
  };
}

export function NotificationConfirmationModal({
  open,
  onClose,
  onConfirm,
  currentUserId,
  listing,
}: NotificationConfirmationModalProps) {
  const isMobile = useMobile();

  const panelTitle = "Get notified when this item is available";
  const panelDescription =
    "We'll send you a notification when this scheduled listing goes live so you don't miss it.";

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const listingPreview = (
    <div className="w-full max-w-[280px] mx-auto">
      <ItemCard
        listingId={listing.id}
        title={listing.title}
        price={listing.price}
        currency={listing.currency ?? null}
        image_urls={listing.image_urls}
        stock={listing.quantity}
        scheduledPublishAt={listing.scheduledPublishAt}
        listingFormat={listing.listing_format ?? null}
        interactive={false}
        cartAction="static"
      />
    </div>
  );

  const guestAuth = (
    <div className="flex flex-col items-center gap-3 w-full">
      <p className="text-sm text-muted-foreground text-center">Please log in to receive notifications</p>
      <div className="w-full space-y-3">
        <Link href="/login" className="block">
          <Button className="w-full rounded-full" size="lg">
            Log in
          </Button>
        </Link>
        <Link href="/register" className="block">
          <Button
            variant="outline"
            className="w-full rounded-full border-foreground/20 bg-transparent text-foreground hover:bg-foreground/10 hover:text-foreground"
            size="lg"
          >
            Register
          </Button>
        </Link>
      </div>
      <p className="text-center text-xs text-muted-foreground/70">
        By continuing, you agree to our{" "}
        <Link href="/terms" className="underline hover:text-foreground" onClick={(e) => e.stopPropagation()}>
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link href="/privacy" className="underline hover:text-foreground" onClick={(e) => e.stopPropagation()}>
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );

  const body = (
    <>
      <p className="text-sm text-muted-foreground text-center">{panelDescription}</p>
      {currentUserId ? (
        <Button className="w-full rounded-full" size="lg" onClick={handleConfirm}>
          <Bell className="h-4 w-4 mr-2" />
          Enable notifications
        </Button>
      ) : (
        guestAuth
      )}
    </>
  );

  if (!open) return null;

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        title={panelTitle}
        zIndex={10100}
        belowTitle={listingPreview}
        contentSized
      >
        <div className="flex flex-col items-center gap-4 pb-2">{body}</div>
      </DockSheet>
    );
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={panelTitle}
      panelClassName="max-w-lg"
    >
      <div className="flex flex-col items-center gap-6">
        {listingPreview}
        {body}
      </div>
    </DialogCard>
  );
}
